import { getCurrentUser } from '@creditsights/api-ui-web';
import { Tooltip, TooltipPosition } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { LockedContent } from '../../../components/LockedContent';
import { useDocumentTitle } from '../../../hooks/common';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';
import {
  hasFinancialsKeyMetrics,
  hasPermissionToFinancials,
  hasPermissionToFinancialsExcelDownload,
} from '../../../utils/permissionsUtils';
import { CompanyOutletContext } from '../Company';

import styles from './CompanyFinancialsTab.module.scss';

const cx = classNames.bind(styles);

const CompanyFinancialsTab = (): React.ReactElement | null => {
  const { issuerId } = useParams();
  const { data: userPermissions } = useFetchUserPermissions();
  const hasExcelDownload = hasPermissionToFinancialsExcelDownload(userPermissions);
  const hasPermission = hasPermissionToFinancials(userPermissions);
  const hasKeyMetrics = hasFinancialsKeyMetrics(userPermissions);
  const { userId } = getCurrentUser();
  const { company } = useOutletContext<CompanyOutletContext>();
  const { hideAsReported = false, hideKeyMetrics = false } = company;
  const [activeTab, setActiveTab] = useState('as-reported');
  useDocumentTitle(`${company.displayName}: Financials`);

  useEffect(() => {
    if (hideAsReported) {
      setActiveTab('key-metrics');
    }
  }, [company]);

  const resolveUrl = () => {
    if (activeTab === 'as-reported') {
      return `/financials/company/${issuerId}/client_timeseries?filter=Annual&ExcelDownload=${hasExcelDownload}&section=FactStream&table=1&userid=${userId}`;
    }

    return `/financials/company/${issuerId}/client_keymetrics?section=FactStream&filter=Annual&exceldownload=${hasExcelDownload}&userid=${userId}`;
  };

  const resolveStyles = () => {
    return { height: `calc(100vh - ${hasKeyMetrics ? '230' : '185'}px)` };
  };

  const renderIframes = () => {
    return (
      <div className="frames-ctx">
        {hasKeyMetrics && (
          <div className={cx('toggle-ctx')}>
            <div className={cx('toggle')}>
              {hideAsReported ? (
                <div className={cx('button', 'gray-out')}>
                  <Tooltip content="As-reported not available" position={TooltipPosition.TopLeft}>
                    <span className="text">As Reported</span>
                  </Tooltip>
                </div>
              ) : (
                <div
                  className={cx(activeTab === 'as-reported' ? 'active-button' : 'inactive', 'button')}
                  onClick={() => setActiveTab('as-reported')}
                >
                  <span className="text">As Reported</span>
                </div>
              )}

              {hideKeyMetrics ? (
                <div className={cx('button', 'gray-out')}>
                  <Tooltip content="Key metrics not available" position={TooltipPosition.TopRight}>
                    <span>
                      <span className="text">Key Metrics</span>
                      <span className={cx('beta')}>beta</span>
                    </span>
                  </Tooltip>
                </div>
              ) : (
                <div
                  className={cx(activeTab === 'key-metrics' ? 'active-button' : 'inactive', 'button')}
                  onClick={() => setActiveTab('key-metrics')}
                >
                  <span className="text">Key Metrics</span> <span className={cx('beta')}>beta</span>
                </div>
              )}
            </div>
          </div>
        )}
        <iframe
          title="company-financials"
          className={cx('frame')}
          src={resolveUrl()}
          style={resolveStyles()}
          data-testid="company-financials-as-ui"
        />
      </div>
    );
  };

  const renderNoPermission = () => {
    return (
      <div className={cx('no-permission')} data-testid="company-financials-tab-no-permission">
        <div className={cx('locked-content')}>
          <LockedContent />
        </div>
      </div>
    );
  };

  if (!issuerId) {
    return null;
  }
  return (
    <div className={cx('body')} data-testid="company-financials-tab">
      {hasPermission ? renderIframes() : renderNoPermission()}
    </div>
  );
};

export default CompanyFinancialsTab;
